import { ExternalLink, Shield } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedCoverageCategoryChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface CoverageCategoryChangedEventProps {
  event: DiscriminatedCoverageCategoryChangedEvent;
  matterId: string;
}

export const CoverageCategoryChangedEvent: React.FC<
  CoverageCategoryChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { coverage, old_category, new_category } = payload;

  // Get carrier name
  const carrierName = coverage.carrier?.name || 'Unknown Carrier';

  // Get policy type
  const policyType = coverage.policy_type
    ? formatPolicyType(coverage.policy_type)
    : null;

  // Get policy identifiers
  const policyNumbers = coverage.policy_identifiers
    ?.map((pi) => pi.identifier_value)
    .filter(Boolean);

  // Format categories
  const oldCategoryLabel = old_category
    ? formatCoverageCategory(old_category)
    : 'None';
  const newCategoryLabel = new_category
    ? formatCoverageCategory(new_category)
    : 'None';

  return (
    <EventContainer>
      <EventHeader
        icon={<Shield size={20} />}
        title="Coverage Category Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Carrier and Policy Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{carrierName}</span>
            {policyType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{policyType}</Badge>
              </>
            )}
          </div>

          {/* Policy Numbers */}
          {policyNumbers && policyNumbers.length > 0 && (
            <div className="text-xs">
              <span className="text-slate-600">Policy Numbers:</span>{' '}
              <span className="font-mono text-[11px]">
                {policyNumbers.join(', ')}
              </span>
            </div>
          )}

          {/* Category Change */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Category:</span>
            <Badge variant="outline" className="line-through">
              {oldCategoryLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant="outline" className="border-green-600">
              {newCategoryLabel}
            </Badge>
          </div>
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/coverage/${coverage.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Coverage
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatPolicyType(type: string): string {
  const types: Record<string, string> = {
    AUTO: 'Auto',
    HOMEOWNERS: 'Homeowners',
    COMMERCIAL: 'Commercial',
    HEALTH: 'Health',
    UMBRELLA: 'Umbrella',
    OTHER: 'Other',
  };
  return types[type] || type;
}

function formatCoverageCategory(category: string): string {
  const categories: Record<string, string> = {
    FIRST_PARTY: '1st Party',
    FIRST_PARTY_HEALTH: '1st Party Health',
    THIRD_PARTY: '3rd Party',
    NOT_LIABLE_INVOLVED_PARTY: 'Not-Liable Involved Party',
    UNCLEAR: 'Unclear',
  };
  return categories[category] || category;
}
